import type { ComponentType, LazyExoticComponent } from 'react';
import type { Layout } from 'react-grid-layout';

import type { FeatureFlagName } from 'utils/features';

/**
 * Widget identifiers — stable contracts referenced by persisted layouts. Never
 * rename a value without a migration in validateAndMigrate.
 */
export type WidgetId =
  | 'critical-damages'
  | 'repair-task-status'
  | 'damage-detection-trend'
  | 'predicted-propagation'
  | 'portfolio-capacity'
  | 'fleet-map'
  | 'attention-required'
  | 'upcoming-tasks'
  | 'defect-patterns'
  | 'benchmark-damage-rate'
  | 'skyspecs-inspection-schedule';

export type WidgetCategory = 'damage' | 'tasks' | 'fleet' | 'work-orders' | 'inspection' | 'cost';

/**
 * 'real' widgets are backed by live queries and ship in the catalog. 'preview'
 * widgets render sample/mock data and are tagged as sample in the catalog.
 */
export type WidgetDataState = 'real' | 'preview';

/** Per-instance, per-widget configuration (e.g. title override, date range). */
export interface WidgetConfig {
  /** Optional user-set title override for this instance. */
  titleOverride?: string;
  [key: string]: unknown;
}

/** A declarative, single-select config option shown in the widget's gear menu. */
export interface WidgetConfigOption {
  key: string;
  label: string;
  options: Array<{ label: string; value: string | number }>;
  defaultValue: string | number;
}

export interface WidgetDefinition {
  id: WidgetId;
  title: string;
  description: string;
  category: WidgetCategory;
  dataState: WidgetDataState;
  /** Lazily-loaded widget body (default export). */
  component: LazyExoticComponent<ComponentType<{ config?: WidgetConfig }>>;
  /** Seed geometry on the 12-column desktop grid. */
  defaultLayout: { w: number; h: number; minW: number; minH: number };
  /** Feature entitlements the account must have, all of them. Empty means ungated. */
  requiredFeatures: FeatureFlagName[];
  /** Show the catalog row even when the account lacks required features. */
  showWhenUnavailable?: boolean;
  /** Keep a placed widget visible so its body can render an entitlement preview. */
  renderWhenUnavailable?: boolean;
  /** When true the widget renders its own card/title chrome; the frame hides its header. */
  selfChrome?: boolean;
  /** Per-instance config options surfaced in the widget's gear menu (enables useful duplicates). */
  configOptions?: WidgetConfigOption[];
}

/** A placed widget. `instanceId` is unique per placement so the same widget can appear twice. */
export interface WidgetInstance {
  instanceId: string;
  widgetId: WidgetId;
  config?: WidgetConfig;
}

/** Bump when the persisted shape changes; validateAndMigrate migrates older states. */
export const DASHBOARD_LAYOUT_VERSION = 2;

/** A react-grid-layout item; `i` equals the WidgetInstance.instanceId. */
export type DashboardLayoutItem = Layout;

export interface DashboardLayoutState {
  version: number;
  /** Placed widget instances, in catalog/insertion order. */
  instances: WidgetInstance[];
  /** Per-breakpoint geometry, keyed by react-grid-layout breakpoint name; items keyed by instanceId. */
  layouts: Record<string, DashboardLayoutItem[]>;
}
